import { useEffect, useMemo } from "react";
import axios from "axios";
import { Alert, Box, Button, Skeleton, Stack, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { useAppSelector } from "../../../../app/hooks";
import PageContainer from "../../../../components/common/PageContainer";
import { routePaths } from "../../../../routes/navigation";
import Client360Panel from "../../Client360Panel";
import Client360HistoryTab from "../history/Client360HistoryTab";
import Client360OrdersTab from "../orders/Client360OrdersTab";
import Client360PurchaseBehavior from "../purchase-behavior/Client360PurchaseBehavior";
import Client360B2bChannelTab from "../channel/Client360B2bChannelTab";
import Client360CdpMasterBanner from "../channel/Client360CdpMasterBanner";
import Client360OnlineChannelTab from "../channel/Client360OnlineChannelTab";
import Client360RetailChannelTab from "../channel/Client360RetailChannelTab";
import Client360MergeSuggestionsCard from "../channel/Client360MergeSuggestionsCard";
import Client360SalesActivityCard from "../sales-activity/Client360SalesActivityCard";
import Client360TeamTab from "../team/Client360TeamTab";
import Client360ProfileTabs from "./Client360ProfileTabs";
import {
    canEditLoyaltyCard,
    parseProfileTab,
    type ClientProfileTab,
} from "./pageHelpers";
import { useClient360Loyalty } from "./useClient360Loyalty";
import { useClient360ProfileData } from "./useClient360ProfileData";
import { useClient360ProfileLinks } from "./useClient360ProfileLinks";
import { useClient360PartnerMerge } from "./useClient360PartnerMerge";
import { usePurchaseBehaviorQuery } from "../purchase-behavior/usePurchaseBehaviorQuery";
import { getApiLoadErrorDetail } from "../../../../lib/api/loadErrorDetail";
import { isValidClientProfileId, normalizeClientProfileId } from "../../../../lib/clientProfileId";
import { readClient360NavigationState } from "../navigationState";

export default function Client360PageContainer() {
    const user = useAppSelector((state) => state.auth.user);
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const theme = useTheme();
    const isCompact = useMediaQuery(theme.breakpoints.down("md"));
    const rawProfileId = searchParams.get("profile") ?? "";
    const seedProfile = readClient360NavigationState(location.state).seedProfile ?? null;
    const profileId = useMemo(() => {
        const fromUrl = normalizeClientProfileId(rawProfileId);
        if (fromUrl) {
            return fromUrl;
        }

        if (seedProfile?.profileId) {
            return normalizeClientProfileId(seedProfile.profileId) ?? "";
        }

        return "";
    }, [rawProfileId, seedProfile?.profileId]);
    const customerFromParams = searchParams.get("customer") ?? "";
    const returnTo = searchParams.get("returnTo") || routePaths.clients;
    const activeTab = parseProfileTab(searchParams.get("view"));

    useEffect(() => {
        if (profileId === "" || profileId === rawProfileId) {
            return;
        }

        const nextParams = new URLSearchParams(searchParams);
        nextParams.set("profile", profileId);
        setSearchParams(nextParams, { replace: true });
    }, [profileId, rawProfileId, searchParams, setSearchParams]);
    const {
        cdpQuery,
        detailSections,
        historyExtrasQuery,
        historyQuery,
        profileQuery,
        profileReady,
        selectedData,
        subsidiaryLoadError,
    } = useClient360ProfileData({
        customerFromParams,
        isCompact,
        profileId,
        user,
    });
    const selectedPartnerId = selectedData?.selectedLoyaltyCard?.partnerId ?? 0;
    const loyalty = useClient360Loyalty({ profileId, selectedPartnerId, enabled: profileReady });
    const profileLinks = useClient360ProfileLinks(profileId);
    const partnerMerge = useClient360PartnerMerge(profileId);
    const purchaseBehaviorQuery = usePurchaseBehaviorQuery({
        activeTab,
        profileId,
        enabled: profileReady,
    });
    const cdpProfile = cdpQuery.data?.cdpProfile ?? null;

    if (profileQuery.isPending && !profileReady) {
        return (
            <Box className="workspace-page client-360-page">
                <PageContainer
                    eyebrow="Клиентски профил"
                    title="Зареждане..."
                    subtitle="Потвърждаваме профила в ERP."
                    action={<Button variant="outlined" onClick={() => navigate(returnTo)}>Назад</Button>}
                >
                    <Stack spacing={2}>
                        <Skeleton variant="rounded" height={120} />
                        <Skeleton variant="rounded" height={280} />
                    </Stack>
                </PageContainer>
            </Box>
        );
    }

    if (profileId === "") {
        return (
            <Client360ErrorPage
                detail={
                    rawProfileId !== "" && !isValidClientProfileId(rawProfileId)
                        ? `Невалиден профил „${rawProfileId}“. Отвори клиента от списъка „Клиенти“ (двоен клик по ред).`
                        : "Липсва параметър profile в адреса. Отвори клиента от списъка „Клиенти“."
                }
                onBack={() => navigate(returnTo)}
            />
        );
    }

    if (profileQuery.isError || (profileQuery.isFetched && !profileReady)) {
        const status =
            profileQuery.error && axios.isAxiosError(profileQuery.error)
                ? profileQuery.error.response?.status
                : undefined;

        return (
            <Client360ErrorPage
                detail={
                    status === 404
                        ? `Профилът „${profileId}“ не е намерен в ERP. Отвори клиента отново от списъка (двоен клик).`
                        : getApiLoadErrorDetail(profileQuery.error, "/api/clients/profile")
                }
                onBack={() => navigate(returnTo)}
            />
        );
    }

    const pageTitle = selectedData?.profile.customer ?? "Клиентски прочит";
    const showPanel = profileReady && Boolean(selectedData);

    function handleTabChange(value: ClientProfileTab) {
        const nextParams = new URLSearchParams(searchParams);
        value === "profile" ? nextParams.delete("view") : nextParams.set("view", value);
        setSearchParams(nextParams, { replace: true });
    }

    return (
        <Box className="workspace-page client-360-page">
            <PageContainer
                eyebrow="Клиентски профил"
                title={pageTitle}
                subtitle="Идентичност, лоялност и последни поръчки."
                action={<Button variant="outlined" onClick={() => navigate(returnTo)}>Назад</Button>}
            >
                {!showPanel ? (
                    <Stack spacing={2}>
                        <Skeleton variant="rounded" height={120} />
                        <Skeleton variant="rounded" height={280} />
                    </Stack>
                ) : null}
                {showPanel && selectedData ? (
                <>
                <Client360ProfileTabs activeTab={activeTab} onChange={handleTabChange} />
                {subsidiaryLoadError ? (
                    <Alert severity="warning" sx={{ mb: 1 }}>
                        Част от данните (CDP, история, свързани) не се заредиха. Презареди страницата или провери достъпа до API.
                    </Alert>
                ) : null}
                {activeTab === "channel-retail" ? (
                    cdpProfile ? (
                        <Stack spacing={2}>
                            <Client360CdpMasterBanner cdp={cdpProfile} />
                            <Client360RetailChannelTab channel={cdpProfile.channels.retail} />
                        </Stack>
                    ) : (
                        <Alert severity="info">CDP данните не са налични. Презареди профила.</Alert>
                    )
                ) : activeTab === "channel-b2b" ? (
                    cdpProfile ? (
                        <Stack spacing={2}>
                            <Client360CdpMasterBanner cdp={cdpProfile} />
                            <Client360B2bChannelTab channel={cdpProfile.channels.b2b} />
                        </Stack>
                    ) : (
                        <Alert severity="info">CDP данните не са налични. Презареди профила.</Alert>
                    )
                ) : activeTab === "channel-online" ? (
                    cdpProfile ? (
                        <Stack spacing={2}>
                            <Client360CdpMasterBanner cdp={cdpProfile} />
                            <Client360OnlineChannelTab channel={cdpProfile.channels.online} />
                        </Stack>
                    ) : (
                        <Alert severity="info">CDP данните не са налични. Презареди профила.</Alert>
                    )
                ) : activeTab === "team" ? (
                    <Client360TeamTab
                        customerName={selectedData.profile.customer ?? ""}
                        profileId={profileId}
                    />
                ) : activeTab === "orders" ? (
                    <Client360OrdersTab orders={selectedData.selectedOrders} />
                ) : activeTab === "history" ? (
                    <Client360HistoryTab
                        orders={selectedData.selectedOrders}
                        profile={selectedData.profile}
                        traderDefect={selectedData.traderDefect}
                        transportError={historyExtrasQuery.data?.transportError}
                        transportHistory={historyExtrasQuery.data?.transportHistory}
                        expoClientObjects={historyExtrasQuery.data?.expoClientObjects}
                    />
                ) : activeTab === "purchase-behavior" ? (
                    <Client360PurchaseBehavior
                        data={purchaseBehaviorQuery.data}
                        isLoading={purchaseBehaviorQuery.isPending || purchaseBehaviorQuery.isFetching}
                        orders={selectedData.selectedOrders}
                        profile={selectedData.profile}
                    />
                ) : (
                    <>
                        {cdpProfile ? <Client360CdpMasterBanner cdp={cdpProfile} /> : null}
                        <Client360SalesActivityCard profileId={profileId} />
                        {cdpProfile?.mergeTableReady ? (
                            <Box sx={{ mb: 2 }}>
                                <Client360MergeSuggestionsCard
                                    canManage={selectedData.canManageRelatedProfiles}
                                    currentProfileId={profileId}
                                    suggestions={cdpProfile.mergeSuggestions}
                                    onApprove={partnerMerge.approve}
                                    onReject={partnerMerge.reject}
                                />
                            </Box>
                        ) : null}
                        <Client360Panel
                        canEditLoyaltyCard={canEditLoyaltyCard(user)}
                        canManageRelatedProfiles={selectedData.canManageRelatedProfiles}
                        detailSections={detailSections}
                        isHistoryLoading={historyQuery.isPending}
                        isLoyaltyAuditLoading={loyalty.eventsQuery.isPending}
                        isLoyaltyCardSaving={loyalty.mutation.isPending}
                        issues={selectedData.selectedIssues}
                        loyaltyAuditEnabled={loyalty.eventsQuery.data?.auditEnabled ?? false}
                        loyaltyAuditEvents={loyalty.eventsQuery.data?.events ?? []}
                        loyaltyCard={loyalty.detailQuery.data?.card ?? selectedData.selectedLoyaltyCard}
                        loyaltyCardSaveError={loyalty.saveError}
                        loyaltyUsageHistory={loyalty.detailQuery.data?.usageHistory ?? []}
                        manualRelatedProfiles={selectedData.manualRelatedProfiles}
                        nextAction={selectedData.nextAction}
                        onAddRelatedProfile={async (relatedProfileId, note) => {
                            await profileLinks.mutateAsync({ action: "create", relatedProfileId, note });
                        }}
                        onRemoveRelatedProfile={async (relatedProfileId) => {
                            await profileLinks.mutateAsync({ action: "delete", relatedProfileId });
                        }}
                        onSaveLoyaltyCard={async (values) => { await loyalty.mutation.mutateAsync(values); }}
                        orders={selectedData.selectedOrders}
                        profile={selectedData.profile}
                        relatedProfiles={selectedData.relatedProfiles}
                        traderDefect={selectedData.traderDefect}
                    />
                    </>
                )}
                </>
                ) : null}
            </PageContainer>
        </Box>
    );
}

function Client360ErrorPage({ detail, onBack }: { detail?: string; onBack: () => void }) {
    return (
        <Box className="workspace-page">
            <PageContainer eyebrow="Клиентски профил" title="Клиентски прочит" action={<Button variant="outlined" onClick={onBack}>Назад към клиентите</Button>}>
                <Alert severity="warning">
                    Не успяхме да заредим избрания клиентски профил.
                    {detail ? ` ${detail}` : " Провери дали има валиден параметър profile и дали backend отговаря."}
                </Alert>
            </PageContainer>
        </Box>
    );
}
